import * as _ from 'lodash';
import { useMemo } from 'react';

import { window } from '../../../../globals';
import { useAsyncRequest } from '../../../../hooks';
import { get } from '../../../../redux/api';
import { useProjectSettingsContext } from '../context';
import type { NotificationUserType } from '../../../../../shared/settings/project/notifications';
import type { MinimalUser } from '../../../../../shared/user';

function fetchUsers() {
  return get<MinimalUser[]>('/api/users/minimal');
}

function getNotificationScanUserType(
  notificationDefaultEmailScanUsers: number[],
  allOrgUsers: MinimalUser[],
): NotificationUserType {
  if (notificationDefaultEmailScanUsers.length === 1 && notificationDefaultEmailScanUsers[0] === window.user?.id) {
    return 'current';
  }

  //  Creates a map to simplify checking if a user is on the notifications user list.
  const notificationsUserIdsSet = new Set(notificationDefaultEmailScanUsers);
  const areAllUsersAreSubscribed = allOrgUsers.every(user => notificationsUserIdsSet.has(user.id));
  return areAllUsersAreSubscribed ? 'all' : 'custom';
}

/**
 * This is the hook for the notifications settings page.
 * It pulls in the editable fields from the project context,
 * along with helpers for updating and saving the data.
 *
 * It also fetches other data which the page depends on and provides
 * it in the return value. Notifications settings depends on:
 * - User list minimal (`/api/users/minimal`)
 */
const useProjectNotificationsSettings = () => {
  const { notifications } = useProjectSettingsContext();

  //  Note that we don't do any transforming/converting to SelectOption here.
  //  This is because users is only needed in ModalEditNotifications, which expects MinimalUser[].
  const usersAsyncValue = useAsyncRequest(fetchUsers);

  const settings = useMemo(() => ({
    notificationDefaultEnabled: notifications.settings.notificationDefaultEnabled,
    notificationDefaultEmailScanUsers: notifications.settings.notificationDefaultEmailScanUsers,
    notificationDefaultSlackScan: notifications.settings.notificationDefaultSlackScan,
    //  Because this depends on the users we fetch in the hook itself, we need to update this
    //  here instead of in the convertNotificationsArrayToSettings function.
    notificationDefaultEmailScanUserType: getNotificationScanUserType(
      notifications.settings.notificationDefaultEmailScanUsers,
      usersAsyncValue.value || [],
    ),
    notificationDefaultApiWebhookScan: notifications.settings.notificationDefaultApiWebhookScan,
  }), [notifications.settings, usersAsyncValue.value]);

  //  This won't really change after users are fetched, so we memoize it.
  const allUserIds = useMemo(() => usersAsyncValue.value?.map(user => user.id) || [], [usersAsyncValue.value]);

  const onUpdateUsersToCurrent = () => notifications.onUpdate({
    notificationDefaultEmailScanUserType: 'current',
    notificationDefaultEmailScanUsers: window.user?.id ? [window.user?.id] : [],
  });

  return {
    settings,
    canUserEditSettings: notifications.canUserEditSettings,
    projectSettingsBaseURL: notifications.projectSettingsBaseURL,
    //  Because there is a second checkbox for "enable notifications" vs "are email notifications enabled",
    //  we need to derive this boolean value for that checkbox to work.
    isEmailNotificationsEnabled: !usersAsyncValue.isLoading &&
    !(
      _.isEmpty(settings.notificationDefaultEmailScanUsers) &&
      settings?.notificationDefaultEmailScanUserType === 'custom'
    ),
    isDirty: notifications.isDirty,
    isSaving: notifications.isSaving,
    onSave: notifications.onSave,
    onUpdate: notifications.onUpdate,
    isLoading: usersAsyncValue.isLoading,
    isSaveButtonDisabled: !notifications.isDirty ||
      usersAsyncValue.isLoading ||
      notifications.isSaving,
    users: usersAsyncValue.value,
    //  These helpers make the component code more readable.
    onUpdateUsersToCurrent,
    onUpdateUsersToAll: () => notifications.onUpdate({
      notificationDefaultEmailScanUserType: 'all',
      notificationDefaultEmailScanUsers: allUserIds,
    }),
    onDisableEmailNotifications: () => notifications.onUpdate({
      notificationDefaultEmailScanUserType: 'custom',
      notificationDefaultEmailScanUsers: [],
    }),
    onInitCustomUsers: () => {
      if (_.isEmpty(settings?.notificationDefaultEmailScanUsers)) {
        // add the current user if the list is empty so the user modal doesn't show up empty
        notifications.onUpdate({
          notificationDefaultEmailScanUserType: 'custom',
          notificationDefaultEmailScanUsers: [window.user?.id as number],
        });
      } else {
        notifications.onUpdate({ notificationDefaultEmailScanUserType: 'custom' });
      }
    },
  };
};

export default useProjectNotificationsSettings;
